import {z} from 'zod';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';

type CamelCasedPropertiesDeep<T> = T extends Array<infer U>
  ? Array<CamelCasedPropertiesDeep<U>>
  : // Don't muck with functions
  T extends Function
  ? T
  : // Don't muck with rich objects
  T extends Date
  ? T
  : T extends RegExp
  ? T
  : T extends Error
  ? T
  : T extends Map<infer K, infer V>
  ? Map<CamelCasedPropertiesDeep<K>, CamelCasedPropertiesDeep<V>>
  : T extends Set<infer U>
  ? Set<CamelCasedPropertiesDeep<U>>
  : T extends WeakMap<infer K, infer V>
  ? WeakMap<CamelCasedPropertiesDeep<K>, CamelCasedPropertiesDeep<V>>
  : T extends WeakSet<infer U>
  ? WeakSet<CamelCasedPropertiesDeep<U>>
  : // Otherwise, transform the object
  T extends object
  ? {
      [K in keyof T as K extends string
        ? CamelCase<K>
        : K]: CamelCasedPropertiesDeep<T[K]>;
    }
  : T;

type CamelCase<S extends string> =
  S extends `${infer P1}_${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
    : Lowercase<S>;

/**
 * If we desire to use Zod and have our property names in camel case,
 * we would need to add every property of the object in camel case.
 *
 * This function wraps your Zod schema and it will magically convert
 * the case of the object from snake to camel. Researchers are still
 * trying to figure out how this function works. It seems was first
 * discovered in Egypt in the middle of the 19th century.
 *
 * @see https://github.com/practicegenius/bedrock/pull/4634#discussion_r2019369283
 */
export const camelizeZodSchema = <T extends z.ZodTypeAny>(
  zod: T
): z.ZodEffects<z.infer<T>, CamelCasedPropertiesDeep<T['_output']>> =>
  zod.transform(
    val => objectKeysToCamelCaseDeep(val) as CamelCasedPropertiesDeep<T>
  );

/**
 * A Zod schema for boolean values.
 * This will auto-convert a string or number value to a boolean during parsing.
 * It will also accept booleans as is.
 * If the value is any other type, it will return false.
 * Once we are on Zod 4, we can look at replacing this with z.stringbool()
 * @see https://github.com/colinhacks/zod/issues/1630
 */
export const booleanSchema = z.preprocess(val => {
  if (typeof val === 'string') {
    const trimmed = val.trim().toLowerCase();
    // Truthy values
    if (
      trimmed === 'true' ||
      trimmed === '1' ||
      trimmed === 'yes' ||
      trimmed === 'on' ||
      trimmed === 'y' ||
      trimmed === 'enabled'
    ) {
      return true;
    }
    // Explicit falsy values
    if (
      trimmed === 'false' ||
      trimmed === '0' ||
      trimmed === 'no' ||
      trimmed === 'off' ||
      trimmed === 'n' ||
      trimmed === 'disabled'
    ) {
      return false;
    }
    // Empty string or unknown strings
    return false;
  }

  if (typeof val === 'number') {
    return val !== 0 && !Number.isNaN(val);
  }

  if (typeof val === 'boolean') {
    return val;
  }

  return false;
}, z.boolean());
